import React, { useEffect, useState } from 'react';
import { getUserFlows, getFlowResponsesByType } from '../../../lib/api';
import './summary.css';
import './detailsReport.css';
import './userFlowResponses.css';

const PAGE_SIZE = 10;

const renderJsonData = (json) => {
  if (!json || typeof json !== 'object') return '—';
  const keys = Object.keys(json).filter(
    (k) => k !== 'flow_type' && k !== 'flow_token'
  );
  if (!keys.length) return '—';

  return (
    <ul className="response-fields">
      {keys.map((key) => (
        <li key={key}>
          <span className="response-field-label">{key.replace(/_/g, ' ')}:</span>{' '}
          <span>{String(json[key] ?? '—')}</span>
        </li>
      ))}
    </ul>
  );
};

const UserFlowResponsesReport = () => {
  const [flows, setFlows] = useState([]);
  const [selectedFlowType, setSelectedFlowType] = useState('');
  const [responses, setResponses] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE });
  const [loadingFlows, setLoadingFlows] = useState(true);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadFlows = async () => {
      setLoadingFlows(true);
      setError('');
      try {
        const res = await getUserFlows();
        if (cancelled) return;
        setFlows(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load user flows.');
      } finally {
        if (!cancelled) setLoadingFlows(false);
      }
    };
    loadFlows();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedFlowType) {
      setResponses([]);
      return undefined;
    }

    let cancelled = false;
    const loadResponses = async () => {
      setLoadingResponses(true);
      setError('');
      try {
        const res = await getFlowResponsesByType(
          selectedFlowType,
          pagination.page,
          pagination.limit
        );
        if (cancelled) return;
        setResponses(Array.isArray(res?.data) ? res.data : []);
        setPagination((prev) => ({
          ...prev,
          total: res?.pagination?.total ?? 0,
          limit: res?.pagination?.limit ?? PAGE_SIZE,
        }));
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load responses.');
      } finally {
        if (!cancelled) setLoadingResponses(false);
      }
    };

    loadResponses();
    return () => { cancelled = true; };
  }, [selectedFlowType, pagination.page, pagination.limit]);

  const handleFlowSelect = (flowType) => {
    setSelectedFlowType(flowType);
    setPagination((prev) => ({ ...prev, page: 1, total: 0 }));
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <div className="summary-wrap">
      <div className="summary-header">
        <h2>WhatsApp Flow Responses</h2>
        <div className="summary-meta">
          {selectedFlowType ? `${responses.length} on this page` : `${flows.length} flows`}
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

      {loadingFlows ? (
        <div className="summary-loading">Loading flows...</div>
      ) : flows.length > 0 ? (
        <div className="flow-tabs">
          {flows.map((flow) => (
            <button
              key={flow.flow_type}
              type="button"
              className={`flow-tab ${selectedFlowType === flow.flow_type ? 'active' : ''}`}
              onClick={() => handleFlowSelect(flow.flow_type)}
            >
              {flow.flow_name || flow.flow_type}
            </button>
          ))}
        </div>
      ) : (
        <div className="empty-cell">No flows found</div>
      )}

      {selectedFlowType && (
        <div className="summary-scroll">
          <div className="summary-table-wrap">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Recipient</th>
                  <th>Submitted At</th>
                  <th>Response</th>
                </tr>
              </thead>
              <tbody>
                {loadingResponses ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="skeleton-row">
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-text" /></td>
                      <td><div className="sk sk-text long" /></td>
                      <td><div className="sk sk-bubble" /></td>
                    </tr>
                  ))
                ) : responses.length ? (
                  responses.map((resp) => (
                    <tr key={resp.id}>
                      <td>{resp.id}</td>
                      <td>{resp.recipient_id || '—'}</td>
                      <td>
                        {resp.message_timestamp
                          ? new Date(resp.message_timestamp).toLocaleString()
                          : '—'}
                      </td>
                      <td>{renderJsonData(resp.response_json)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="empty-cell">No responses found for this flow</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!loadingResponses && pagination.total > pagination.limit && (
            <div className="pagination-controls">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={pagination.page >= totalPages}
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {!selectedFlowType && !loadingFlows && flows.length > 0 && (
        <div className="flow-select-hint">Select a flow above to view responses.</div>
      )}
    </div>
  );
};

export default UserFlowResponsesReport;
